import { useTranslation } from 'react-i18next';
import { formatProductsPhrase, isCapTier, planDisplayName, type PlanInfo } from '@/lib/plans';

interface PlanPriceCardProps {
  plan: PlanInfo;
  /** Pre-resolved display price (already picked EUR/USD by the caller). */
  price: string;
  /** Shows the "Recommended" chip. Callers pass
   * `recommendedPlan && recommendedPlan.tier !== nextPlan?.tier` — true only
   * when getRecommendedPlan() picked a plan beyond the plain next tier up. */
  showRecommendedBadge?: boolean;
}

/**
 * Shared plan price card (Planname + optionaler "Empfohlen"-Chip,
 * Produkt-Kontingent, Preis, Jahresrabatt-Hinweis). Originally inline in
 * UpgradeModal.tsx (the RED limit-reached modal); extracted so the YELLOW
 * QuotaLimitDialog can show the identical card (C-21) without drifting.
 *
 * Preisumbau: bei Kapazitaets-Baendern IST der Name die Kapazitaet
 * ("1.000 Produkte"). Die Kontingent-Zeile wuerde denselben Satz wiederholen —
 * sie sagt dort stattdessen nur noch "Alle Funktionen inklusive". Legacy-Tarife
 * (Eigennamen) behalten die alte Zweiteilung Name + Kontingent.
 */
export function PlanPriceCard({ plan, price, showRecommendedBadge = false }: PlanPriceCardProps) {
  const { t, i18n } = useTranslation('enforcement');
  const isBand = isCapTier(plan.tier);

  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-primary">
          {/* Band: der Name IST die Kapazitaet — "1.000 Produkte Tarif" waere
              gestelzt, der Name steht allein. Legacy: "{{plan}} Tarif". */}
          {isBand
            ? planDisplayName(plan, i18n?.language, t)
            : t('modal.planName', { plan: plan.name })}
        </h4>
        {showRecommendedBadge && (
          <span className="text-[10px] font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            {t('status.recommended', { ns: 'common' })}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        {isBand
          ? t('modal.allFeaturesIncluded')
          : t('modal.productsIncluded', { label: formatProductsPhrase(plan, i18n?.language, t) })}
      </p>
      <p className="text-lg font-bold">
        {price}
        <span className="text-sm font-normal text-muted-foreground">{t('modal.perMonth')}</span>
      </p>
      <p className="text-xs text-muted-foreground">{t('modal.annualDiscount')}</p>
    </div>
  );
}
